import {
  ArrowLeftOutlined,
  CaretRightFilled,
  EditOutlined,
  MoreOutlined,
  PauseOutlined,
} from '@ant-design/icons';
import { Button, Circle } from '@mm/components';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { TooltipPlacement } from 'antd/es/tooltip';
import classNames from 'classnames';
import React, { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { PrimaryButton } from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import TextInput from '../../../../../../../common/components/TextInput';
import { BotBuilderRoutes } from '../../../../../../../routes/bot-builder-routes';
import {
  INodeMenuItem,
  Menu,
} from '../../AutomationEdit/DialogueEditor/NodeTypes/elements/NodeMenu/NodeMenu';
import {
  AutomationState,
  useAutomation,
} from '../../AutomationEdit/use-automation';
import { useAutomationStatus } from '../../use-automation-status';
import styles from './AutomationHeader.module.css';

export const AutomationHeader = (props: {
  state: AutomationState;
  actions?: ReturnType<typeof useAutomation>['actions'];
  onStart?: () => void;
  menu?: INodeMenuItem[];
  withTooltip?: (
    children: ReactNode,
    placement?: TooltipPlacement,
  ) => ReactNode;
  disabledActions?: boolean;
}) => {
  const { state, actions, onStart, menu, withTooltip, disabledActions } = props;
  const chatbotId = useSelector(activeChatbotIdSelector);

  const history = useHistory();
  const automationStatus = useAutomationStatus();

  const status = automationStatus.get(state.source);

  return (
    <header className={styles.header}>
      <div className={styles.actions}>
        <div
          className={styles.back}
          onClick={() =>
            history.push(
              BotBuilderRoutes.automationHome.getLink({
                chatbotId,
              }),
            )
          }
        >
          <ArrowLeftOutlined />
          Back
        </div>

        {state.busy !== 'editing' && (
          <span className={styles.title}>
            <abbr title={state.automation?.name}>{state.automation?.name}</abbr>
          </span>
        )}

        {state.busy === 'editing' && (
          <div>
            <TextInput
              className={styles.name}
              placeholder="New automation"
              value={state.automation?.name}
              onChange={e => actions?.setName(e.target.value)}
              maxLength={100}
            />
          </div>
        )}

        {state.busy === 'editing' && (
          <>
            <Button
              disabled={
                !!state.automation?.name?.length &&
                state.automation?.name?.toString().trim() ===
                  state.source?.name?.toString().trim()
              }
              onClick={() => actions?.saveName()}
            >
              Save
            </Button>
            <Button onClick={() => actions?.busy('')} color="secondary-blue">
              Cancel
            </Button>
          </>
        )}

        {state.busy !== 'editing' && state.busy !== 'new' && (
          <EditOutlined
            className={styles.edit}
            onClick={() => actions?.busy('editing')}
          />
        )}
      </div>

      {withTooltip?.(
        <div className={styles.actions}>
          {!status.active && (
            <PrimaryButton
              onClick={onStart}
              className={classNames(styles.startButton, {
                [styles.diabledStartButton]: disabledActions,
              })}
              disabled={state.busy === 'new'}
            >
              <CaretRightFilled className={styles.buttonIcon} />
              Start
            </PrimaryButton>
          )}

          {status.active && !status.paused && (
            <>
              <div className={styles.activeLabel}>
                <Circle color="#389e0d" className={styles.statusCircle} />
                Active
              </div>
              <PrimaryButton
                onClick={actions?.pause}
                className={styles.pauseButton}
              >
                <PauseOutlined className={styles.buttonIcon} />
                Pause
              </PrimaryButton>
            </>
          )}

          {status.active && status.paused && (
            <PrimaryButton
              onClick={onStart}
              className={classNames(styles.startButton, {
                [styles.diabledStartButton]: disabledActions,
              })}
            >
              <CaretRightFilled className={styles.buttonIcon} />
              Resume
            </PrimaryButton>
          )}

          {menu?.length && state.busy !== 'new' ? (
            <Menu
              className={classNames(styles.menu, {
                [styles.disabledMenu]: disabledActions,
              })}
              menu={menu}
              direction="bottom"
              menuButton={
                <Button
                  css={{
                    container: styles.menuButton,
                  }}
                  color="secondary"
                >
                  <MoreOutlined />
                </Button>
              }
            />
          ) : null}
        </div>,
        'bottom',
      )}
    </header>
  );
};
